import type { IsoTimestamp } from "../../domain/time";
import type { LocalSale, LocalSaleLine } from "../../data/local/db";

/**
 * Ticket composition (T002, FR-202, FR-203, FR-205).
 *
 * THE RULE THAT MATTERS, mirrored from compose.ts's composeReceipt: a ticket is
 * composed ONLY from values stored on the sale — the *NameSnapshot fields —
 * never from live catalog state. That is what makes a menu rename between
 * order and print unable to change what a ticket already said.
 *
 * NO MONEY FIELD EXISTS ON THIS TYPE (FR-205), unlike ReceiptLine/
 * ReceiptDocument. A production docket that could show a price invites being
 * handed to a customer as proof of payment, which in the Philippines is a
 * regulated document this is not. Enforced by the type, not by a rendering
 * rule someone could forget.
 */

export interface TicketLine {
  lineNumber: number;
  name: string;
  variant: string | null;
  modifiers: string[];
  note: string | null;
  quantity: number;
}

export interface TicketDocument {
  orderNumber: string;
  issuedAt: IsoTimestamp;
  cashier: string;
  orderType: string;
  customerName: string | null;
  lines: TicketLine[];
}

export interface ComposeTicketInput {
  sale: LocalSale;
  lines: LocalSaleLine[];
  cashierName: string;
  /**
   * Assigned by order-numbers.ts, not derived here — composeTicket has no
   * opinion about shift-scoped sequencing, only about what a ticket says once
   * it has a number.
   */
  orderNumber: string;
}

export function composeTicket(input: ComposeTicketInput): TicketDocument {
  const { sale } = input;

  const lines: TicketLine[] = [...input.lines]
    .sort((a, b) => a.lineNumber - b.lineNumber)
    .map((line) => ({
      lineNumber: line.lineNumber,
      // Snapshots, not current catalog values — see the module doc above.
      name: line.itemNameSnapshot,
      variant: line.variantNameSnapshot,
      modifiers: line.modifiers.map((modifier) => modifier.nameSnapshot),
      note: line.note,
      quantity: line.quantity,
    }));

  return {
    orderNumber: input.orderNumber,
    issuedAt: sale.createdAt,
    cashier: input.cashierName,
    orderType: sale.orderType,
    customerName: sale.customerName,
    lines,
  };
}
